import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, XCircle, Circle, ShoppingCart, Ban } from 'lucide-react';
import {
  purchaseRequests as purchaseRequestsApi, type PurchaseRequest, type PurchaseRequestStep, type PurchaseRequestEvent,
} from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { PR_STATUS_META } from '../../lib/purchaseRequestStatus';

const EVENT_LABELS: Record<string, string> = {
  submitted: 'Submitted',
  step_approved: 'Approved',
  step_rejected: 'Rejected',
  auto_approved: 'Auto-approved',
  converted_to_expense: 'Converted to an expense claim',
  cancelled: 'Cancelled by submitter',
};

export default function PurchaseRequestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [purchaseRequest, setPurchaseRequest] = useState<PurchaseRequest | null>(null);
  const [steps, setSteps] = useState<PurchaseRequestStep[]>([]);
  const [events, setEvents] = useState<PurchaseRequestEvent[]>([]);
  const [approvers, setApprovers] = useState<{ id: string; full_name: string | null; email: string }[]>([]);
  const [convertedExpenseId, setConvertedExpenseId] = useState<string | null>(null);
  const [canActAsApprover, setCanActAsApprover] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comment, setComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await purchaseRequestsApi.get(id!);
      setPurchaseRequest(data.purchaseRequest);
      setSteps(data.steps);
      setEvents(data.events);
      setApprovers(data.approvers);
      setConvertedExpenseId(data.convertedExpenseId);
      setCanActAsApprover(data.canActAsApprover);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (fn: () => Promise<unknown>) => {
    setActionLoading(true);
    setError('');
    try {
      await fn();
      setComment('');
      await load();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600" /></div>;
  if (error && !purchaseRequest) return <div className="card text-red-600 text-center py-10">{error}</div>;
  if (!purchaseRequest || !user) return null;

  const meta = PR_STATUS_META[purchaseRequest.status];
  const approverName = (userId: string | null) => {
    if (!userId) return 'Unassigned';
    return approvers.find(a => a.id === userId)?.full_name || approvers.find(a => a.id === userId)?.email || 'Unknown';
  };
  const isCurrentApprover = canActAsApprover;
  const isSubmitter = purchaseRequest.submitter_user_id === user.id;
  const canCancel = isSubmitter && purchaseRequest.status === 'pending_approval';
  const canConvert = isSubmitter && purchaseRequest.status === 'approved' && !convertedExpenseId;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/purchase-requests/mine" className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Purchase Request</h1>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

      <div className="card space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-2xl font-bold text-gray-900 tabular-nums">{purchaseRequest.estimated_amount.toFixed(2)}</p>
            <p className="text-gray-500 text-sm">{purchaseRequest.item_description}</p>
          </div>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${meta.className}`}>{meta.label}</span>
        </div>
        {purchaseRequest.justification && (
          <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{purchaseRequest.justification}</p>
        )}
        {purchaseRequest.needed_by_date && (
          <dl className="text-sm">
            <dt className="text-gray-400 inline">Needed by </dt>
            <dd className="text-gray-900 font-medium inline">{purchaseRequest.needed_by_date}</dd>
          </dl>
        )}
      </div>

      <div className="card space-y-3">
        <h2 className="font-semibold text-gray-900">Approval Stages</h2>
        <div className="space-y-1">
          {steps.map(step => {
            const isRejected = step.status === 'rejected';
            const isApproved = step.status === 'approved';
            const isCurrent = step.status === 'current';
            const isSkipped = step.status === 'skipped';
            const Icon = isApproved ? CheckCircle2 : isRejected ? XCircle : isSkipped ? Ban : Circle;
            const iconClass = isApproved ? 'text-green-500' : isRejected ? 'text-red-500' : isCurrent ? 'text-amber-500' : 'text-gray-300';
            return (
              <div key={step.id} className="flex items-center gap-3 py-2">
                <Icon className={`w-5 h-5 flex-shrink-0 ${iconClass}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${isSkipped ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                    Step {step.step_order}: {approverName(step.approver_user_id)}
                  </p>
                  {step.comment && <p className="text-xs text-gray-500 mt-0.5">{step.comment}</p>}
                </div>
                {isCurrent && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 flex-shrink-0">Current</span>}
              </div>
            );
          })}
        </div>
      </div>

      {isCurrentApprover && (
        <div className="card space-y-3">
          <h2 className="font-semibold text-gray-900">Your approval</h2>
          <textarea
            className="input-field resize-none"
            placeholder="Comment (required to deny)"
            rows={2}
            value={comment}
            onChange={e => setComment(e.target.value)}
          />
          <div className="flex gap-3">
            <button
              onClick={() => runAction(() => purchaseRequestsApi.approve(purchaseRequest.id, comment || undefined))}
              className="btn-primary flex items-center gap-2 flex-1 justify-center"
              disabled={actionLoading}
            >
              <CheckCircle2 className="w-4 h-4" /> Approve
            </button>
            <button
              onClick={() => {
                if (!comment) { setError('A comment is required to deny'); return; }
                runAction(() => purchaseRequestsApi.reject(purchaseRequest.id, comment));
              }}
              className="btn-danger flex items-center gap-2 flex-1 justify-center"
              disabled={actionLoading}
            >
              <XCircle className="w-4 h-4" /> Deny
            </button>
          </div>
        </div>
      )}

      {canConvert && (
        <div className="card">
          <button
            onClick={() => navigate('/expenses/new', {
              state: {
                fromPurchaseRequest: {
                  id: purchaseRequest.id,
                  department_id: purchaseRequest.department_id,
                  description: purchaseRequest.item_description,
                  amount: purchaseRequest.estimated_amount,
                },
              },
            })}
            className="btn-primary flex items-center gap-2 w-full justify-center"
          >
            <ShoppingCart className="w-4 h-4" /> Convert to Expense Claim
          </button>
        </div>
      )}
      {convertedExpenseId && (
        <div className="card">
          <Link to={`/expenses/${convertedExpenseId}`} className="btn-secondary flex items-center gap-2 w-full justify-center">
            <ShoppingCart className="w-4 h-4" /> View Converted Expense Claim
          </Link>
        </div>
      )}

      {canCancel && (
        <div className="card">
          <button
            onClick={() => runAction(() => purchaseRequestsApi.cancel(purchaseRequest.id))}
            className="btn-secondary flex items-center gap-2 w-full justify-center text-red-600"
            disabled={actionLoading}
          >
            <Ban className="w-4 h-4" /> Cancel Request
          </button>
        </div>
      )}

      <div className="card space-y-4">
        <h2 className="font-semibold text-gray-900">History</h2>
        <div className="space-y-3">
          {events.map(ev => (
            <div key={ev.id} className="flex items-start gap-3 text-sm">
              <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">{EVENT_LABELS[ev.event_type] || ev.event_type}</p>
                <p className="text-gray-400 text-xs">{new Date(ev.created_at * 1000).toLocaleString()}</p>
                {ev.comment && <p className="text-gray-600 mt-1">{ev.comment}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
